import { formatDate } from '@/lib/date';
import { STATUS_BUCKET_LABEL, PROVIDER_LABELS } from './account-status';
import { OUTREACH_READINESS_LABEL } from './setup-readiness';
import { WITHOUT_TAGS_FILTER_VALUE } from './tags/tag-helpers';
import {
  type EmailAccountProviderFilter,
  type EmailAccountsSearch,
} from './search-schema';

// Provider value → display label. The single source for provider copy across
// the chip-row deriver, the filter drawer's option list, and the empty state.
export const PROVIDER_FILTER_LABELS: Record<EmailAccountProviderFilter, string> = {
  all: 'All providers',
  gmail: 'Gmail',
  google_workspace: 'Google Workspace',
  outlook: 'Outlook',
  ms365: 'MS365',
  azure: PROVIDER_LABELS.AZURE,
  others: PROVIDER_LABELS.OTHERS,
  zoho: PROVIDER_LABELS.ZOHO,
  yahoo: PROVIDER_LABELS.YAHOO,
  sendgrid: PROVIDER_LABELS.SENDGRID,
  mailgun: PROVIDER_LABELS.MAILGUN,
  amazon_ses: PROVIDER_LABELS.AMAZON_SES,
  elastic_email: PROVIDER_LABELS.ELASTIC_EMAIL,
  yandex: PROVIDER_LABELS.YANDEX,
  godaddy: PROVIDER_LABELS.GODADDY,
};

/** ESP filter value → display label. */
const ESP_LABEL: Record<NonNullable<EmailAccountsSearch['esp']>, string> = {
  auto: 'Auto',
  custom: 'Custom',
};

// Mirrors `ESP_VOLUME_FIELD_LABELS` in `filters/filter-helpers.ts`; declared
// locally to avoid a circular import (that file already imports from here).
const ESP_VOLUME_CHIP_LABEL = {
  google: 'Google',
  microsoft: 'Microsoft',
  other: 'Others',
} as const;

/** "Jan 5, 2026" from an ISO `YYYY-MM-DD`, parsed as parts (no UTC shift). */
function formatIsoDateLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return formatDate(new Date(y, m - 1, d));
}

/** Render a one-sided or two-sided numeric range as "a–b" / "≥ a" / "≤ b". */
function rangeLabel(min: number | undefined, max: number | undefined, suffix = ''): string {
  if (min !== undefined && max !== undefined) return `${min}${suffix}–${max}${suffix}`;
  if (min !== undefined) return `≥ ${min}${suffix}`;
  return `≤ ${max}${suffix}`;
}

/** Render a one-sided or two-sided date range as "a – b" / "From a" / "Until b". */
function dateRangeLabel(from: string | undefined, to: string | undefined): string {
  if (from && to) return `${formatIsoDateLabel(from)} – ${formatIsoDateLabel(to)}`;
  if (from) return `From ${formatIsoDateLabel(from)}`;
  return `Until ${formatIsoDateLabel(to as string)}`;
}

// Filters set via the drawer (everything except the search box). Drives the
// Filter button's active tint and is a subset of `hasFilters`. Mirrors the
// per-key checks in `deriveActiveFilters` so the toolbar tint, the Clear-filters
// affordance, and the chip row never disagree about what counts as "active".
export function hasDrawerFilters(search: EmailAccountsSearch): boolean {
  return (
    (search.status?.length ?? 0) > 0 ||
    (search.provider?.length ?? 0) > 0 ||
    (search.tags?.length ?? 0) > 0 ||
    search.esp !== undefined ||
    search.maxLimitMin !== undefined ||
    search.maxLimitMax !== undefined ||
    (search.domain?.trim().length ?? 0) > 0 ||
    search.startDateFrom !== undefined ||
    search.startDateTo !== undefined ||
    search.deliverabilityMin !== undefined ||
    search.deliverabilityMax !== undefined ||
    (search.outreachReadiness?.length ?? 0) > 0 ||
    search.setupScoreMin !== undefined ||
    search.setupScoreMax !== undefined ||
    (search.espGoogleMin ??
      search.espGoogleMax ??
      search.espMicrosoftMin ??
      search.espMicrosoftMax ??
      search.espOthersMin ??
      search.espOthersMax) !== undefined ||
    search.scheduledToday === true
  );
}

/**
 * How many of the filter drawer's SECTIONS currently hold a value — the number
 * on the Filters button's badge.
 *
 * Counts sections, not values: Status with `active` + `paused` selected is
 * **1**, not 2. The question the badge answers is "how many things have I
 * narrowed by", and a per-value count would read as though picking a second
 * status narrowed the list further when it actually widens it.
 *
 * The eleven terms below are the eleven fields `FilterBuilderDialog` offers (`FILTER_FIELD_KEYS`), in its
 * order. `EspValue` is one field holding both the mode and the three
 * per-provider volume ranges, so all seven of those keys collapse into a single
 * term here — even though `deriveActiveFilters` emits up to four separate chips
 * for them, because a chip is a thing you remove and a section is a thing you
 * set.
 *
 * Deliberately excludes `q`: the search box is not in the drawer, and the
 * badge sits on the button that opens it. That also keeps this in lockstep
 * with {@link hasDrawerFilters} — the badge is non-zero exactly when the
 * button is tinted, which is an invariant worth preserving (see the
 * `mirrors the per-key checks` note above). Add a drawer section, add a term
 * to BOTH.
 */
export function countActiveFilterSections(search: EmailAccountsSearch): number {
  const sections = [
    (search.outreachReadiness?.length ?? 0) > 0,
    (search.status?.length ?? 0) > 0,
    (search.provider?.length ?? 0) > 0,
    (search.tags?.length ?? 0) > 0,
    search.maxLimitMin !== undefined || search.maxLimitMax !== undefined,
    search.esp !== undefined ||
      search.espGoogleMin !== undefined ||
      search.espGoogleMax !== undefined ||
      search.espMicrosoftMin !== undefined ||
      search.espMicrosoftMax !== undefined ||
      search.espOthersMin !== undefined ||
      search.espOthersMax !== undefined,
    (search.domain?.trim().length ?? 0) > 0,
    search.startDateFrom !== undefined || search.startDateTo !== undefined,
    search.setupScoreMin !== undefined || search.setupScoreMax !== undefined,
    search.deliverabilityMin !== undefined || search.deliverabilityMax !== undefined,
    search.scheduledToday === true,
  ];
  return sections.filter(Boolean).length;
}

/** Any active filter — a non-blank search box or any drawer filter. */
export function hasAnyFilters(search: EmailAccountsSearch): boolean {
  return (search.q?.trim().length ?? 0) > 0 || hasDrawerFilters(search);
}

export type ActiveFilter = {
  /** Stable React key. 'q' | 'status' | 'provider' | `tag:${name}`. */
  key: string;
  /** Filter name shown first, e.g. 'Search', 'Status', 'Provider', 'Tag'. */
  label: string;
  /** Filter value shown after the name, e.g. 'acme', 'Healthy', 'Gmail', tag name. */
  value: string;
  /** Partial search patch applied on remove (caller always merges `{ page: 1 }`). */
  patch: Partial<EmailAccountsSearch>;
};

// Derive the removable active-filter chips from the current URL search. Mirrors
// `hasFilters`/`clearFilters` in the table: active when `q` is non-blank, and
// one chip per selected status / provider / tag value. Order is q → status(es)
// → provider(s) → tags (array order). Each multi-value chip precomputes its
// post-removal `rest` (dropping just that value) so the chip-row component stays
// presentational.
export function deriveActiveFilters(search: EmailAccountsSearch): ActiveFilter[] {
  const filters: ActiveFilter[] = [];

  const q = search.q?.trim();
  if (q) {
    filters.push({ key: 'q', label: 'Search', value: q, patch: { q: undefined } });
  }

  const statuses = search.status ?? [];
  for (const value of statuses) {
    const rest = statuses.filter((v) => v !== value);
    filters.push({
      key: `status:${value}`,
      label: 'Status',
      value: STATUS_BUCKET_LABEL[value],
      patch: { status: rest.length ? rest : undefined },
    });
  }

  const providers = search.provider ?? [];
  for (const value of providers) {
    const rest = providers.filter((v) => v !== value);
    filters.push({
      key: `provider:${value}`,
      label: 'Provider',
      value: PROVIDER_FILTER_LABELS[value],
      patch: { provider: rest.length ? rest : undefined },
    });
  }

  // `name` may be the reserved `WITHOUT_TAGS_FILTER_VALUE` sentinel — give it
  // its own display label; removal works the same as any other tag entry.
  const tags = search.tags ?? [];
  for (const name of tags) {
    const rest = tags.filter((n) => n !== name);
    filters.push({
      key: `tag:${name}`,
      label: 'Tag',
      value: name === WITHOUT_TAGS_FILTER_VALUE ? 'Without Tags' : name,
      patch: { tags: rest.length ? rest : undefined },
    });
  }

  const outreachReadiness = search.outreachReadiness ?? [];
  for (const value of outreachReadiness) {
    const rest = outreachReadiness.filter((v) => v !== value);
    filters.push({
      key: `outreachReadiness:${value}`,
      label: 'Outreach readiness',
      value: OUTREACH_READINESS_LABEL[value],
      patch: { outreachReadiness: rest.length ? rest : undefined },
    });
  }

  // ── Filter-drawer chips (TRU-326). Each removal patches its key(s) to
  // `undefined`; a range chip clears both bounds at once. ──
  if (search.esp) {
    filters.push({
      key: 'esp',
      label: 'ESP',
      value: ESP_LABEL[search.esp],
      // Clearing the mode also clears the ESP rows — chip removal bypasses
      // the drawer, so it must reproduce that coupling here too.
      patch: {
        esp: undefined,
        espGoogleMin: undefined,
        espGoogleMax: undefined,
        espMicrosoftMin: undefined,
        espMicrosoftMax: undefined,
        espOthersMin: undefined,
        espOthersMax: undefined,
      },
    });
  }

  const domain = search.domain?.trim();
  if (domain) {
    filters.push({
      key: 'domain',
      label: 'Domain',
      value: domain,
      patch: { domain: undefined },
    });
  }

  if (search.maxLimitMin !== undefined || search.maxLimitMax !== undefined) {
    filters.push({
      key: 'maxLimit',
      label: 'Max limit',
      value: rangeLabel(search.maxLimitMin, search.maxLimitMax),
      patch: { maxLimitMin: undefined, maxLimitMax: undefined },
    });
  }

  if (search.startDateFrom || search.startDateTo) {
    filters.push({
      key: 'startDate',
      label: 'Start date',
      value: dateRangeLabel(search.startDateFrom, search.startDateTo),
      patch: { startDateFrom: undefined, startDateTo: undefined },
    });
  }

  if (search.setupScoreMin !== undefined || search.setupScoreMax !== undefined) {
    filters.push({
      key: 'setupScore',
      label: 'Setup score',
      value: rangeLabel(search.setupScoreMin, search.setupScoreMax, '%'),
      patch: { setupScoreMin: undefined, setupScoreMax: undefined },
    });
  }

  if (search.deliverabilityMin !== undefined || search.deliverabilityMax !== undefined) {
    filters.push({
      key: 'deliverability',
      label: 'Deliverability',
      value: rangeLabel(search.deliverabilityMin, search.deliverabilityMax, '%'),
      patch: { deliverabilityMin: undefined, deliverabilityMax: undefined },
    });
  }

  // One chip per active ESP-percentage pair; removal clears only that pair.
  if (search.espGoogleMin !== undefined || search.espGoogleMax !== undefined) {
    filters.push({
      key: 'espVolume:google',
      label: 'ESP',
      value: `${ESP_VOLUME_CHIP_LABEL.google} ${rangeLabel(search.espGoogleMin, search.espGoogleMax, '%')}`,
      patch: { espGoogleMin: undefined, espGoogleMax: undefined },
    });
  }

  if (search.espMicrosoftMin !== undefined || search.espMicrosoftMax !== undefined) {
    filters.push({
      key: 'espVolume:microsoft',
      label: 'ESP',
      value: `${ESP_VOLUME_CHIP_LABEL.microsoft} ${rangeLabel(search.espMicrosoftMin, search.espMicrosoftMax, '%')}`,
      patch: { espMicrosoftMin: undefined, espMicrosoftMax: undefined },
    });
  }

  if (search.espOthersMin !== undefined || search.espOthersMax !== undefined) {
    filters.push({
      key: 'espVolume:other',
      label: 'ESP',
      value: `${ESP_VOLUME_CHIP_LABEL.other} ${rangeLabel(search.espOthersMin, search.espOthersMax, '%')}`,
      patch: { espOthersMin: undefined, espOthersMax: undefined },
    });
  }

  if (search.scheduledToday === true) {
    filters.push({
      key: 'scheduledToday',
      label: 'Schedule',
      value: 'Scheduled today',
      patch: { scheduledToday: undefined },
    });
  }

  return filters;
}
